import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import UnfoldMoreIcon from '@assets/buttons-icons/expand-up-down.svg?react';
import palette from '@lib/core/src/theme/palette';
import AddIcon from '@mui/icons-material/Add';
import { Box, Popover, Typography } from '@mui/material';

import { MeetingTemplateDto } from '@/api/generated';
import { TemplateCard } from '@/components/Agents/AgentTemplate/TemplateCard';
import {
  SelectTemplateButton,
  SelectTemplateButtonContainer
} from '@/components/Meeting/components/MeetingOverview/MeetingOverviewStyles';
import ActivateTemplateCard from '@/components/modals/AgentsModal/ActivateTemplateCard';
import { AgentTemplateIconsMap } from '@/enums/AgentTemplateIconsMap';
import RoutesMap from '@/enums/RoutesMap';

interface TemplateSelectorProps {
  selectedTemplate: MeetingTemplateDto | null | undefined;
  userAgentActiveTemplate: MeetingTemplateDto[] | undefined;
  onTemplateClick: (template: MeetingTemplateDto) => void;
  isAnalyzing: boolean;
}

const TemplateSelector = ({
  selectedTemplate,
  userAgentActiveTemplate,
  onTemplateClick,
  isAnalyzing
}: TemplateSelectorProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const shouldShowActivateCard = !userAgentActiveTemplate || userAgentActiveTemplate.length <= 1;

  const navigateToAgents = () => {
    setAnchorEl(null);
    navigate(RoutesMap.JamiesCapabilities);
  };

  return (
    <SelectTemplateButtonContainer>
      <SelectTemplateButton onClick={handleClick} disabled={isAnalyzing}>
        {selectedTemplate?.name} <UnfoldMoreIcon style={{ marginLeft: '5px' }} fontSize="small" />
      </SelectTemplateButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {}
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box sx={{ maxHeight: '250px', overflowY: 'auto' }}>
          {userAgentActiveTemplate?.map(template => (
            <TemplateCard
              key={template.id}
              icon={AgentTemplateIconsMap[template.name || '']}
              containerStyle={{ border: '0 none', padding: '12px 16px' }}
              name={template.name || ''}
              description={template.context || ''}
              showTags={false}
              isSelected={selectedTemplate?.id === template.id}
              onClick={() => {
                onTemplateClick(template);
                setAnchorEl(null);
              }}
            />
          ))}

          {shouldShowActivateCard ? (
            <ActivateTemplateCard onActivate={navigateToAgents} />
          ) : (
            <Box
              onClick={navigateToAgents}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: '12px 16px',
                cursor: 'pointer',
                borderTop: `1px solid ${palette.grey[200]}`,
                '&:hover': {
                  background: `linear-gradient(0deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%, ${palette.white})`
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  backgroundColor: palette.grey[100],
                  borderRadius: '4px',
                  mr: '16px'
                }}
              >
                <AddIcon sx={{ color: palette.grey[500] }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: palette.grey[500]
                }}
              >
                Create new template
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </SelectTemplateButtonContainer>
  );
};

export default TemplateSelector;
